// imports
import { FC, Fragment, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Autocomplete, Box, CircularProgress, MenuItem, TextField } from '@mui/material';
//interfaces , constants , helper, svgs, graphql
import { SearchInputIcon } from 'assets/svgs';
import { renderIngredients } from 'lib/helper';
import { HTTP_STATUS } from 'constants/index';
import { useDebounce } from 'hooks/useDebounce';
import { IngredientItemFormType, IngredientSelectorProps, Nullable } from 'interfaces';
import {
  IngredientsPayload,
  FormulaIngredientUnit,
  useFindAllIngredientsForSelectorLazyQuery,
  IngredientType,
} from 'generated/graphql';

const IngredientSelector: FC<IngredientSelectorProps> = ({
  name,
  title,
  disabled,
  primaryId,
  isRequired,
  secondaryId,
  type: ingredientType,
  handleChange,
  margin = 'dense',
  isMultiple = false,
}) => {
  const observer = useRef<Nullable<IntersectionObserver>>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [getLoading, setGetLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<IngredientsPayload['data']>([]);
  const [selectedOptions, setSelectedOptions] = useState<IngredientItemFormType[]>([]);
  const debounceValue = useDebounce(searchQuery);

  const [findAll, { loading }] = useFindAllIngredientsForSelectorLazyQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllIngredients } = data;
      const { data: ingredients, response, pagination } = findAllIngredients || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};
        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((ingredients as IngredientsPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      } else {
        setGetLoading(false);
      }
    },
    onError: () => {
      setGetLoading(false);
      setOptions([]);
    },
  });

  const fetchIngredients = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await findAll({
        variables: {
          findAllIngredientsInput: {
            paginationOptions: { page: page || 1, limit: 50 },
            search: query,
            isActive: true,
            ...(primaryId && { categoryId: primaryId }),
            ...(secondaryId && { subCategoryId: secondaryId }),
            ...(ingredientType?.length && {
              type: ingredientType?.map((item) => item.value as IngredientType),
            }),
          },
        },
      });
    },
    [findAll, primaryId, secondaryId, ingredientType],
  );

  useEffect(() => {
    fetchIngredients();
  }, [fetchIngredients]);

  const lastElementRef = useCallback(
    (node: HTMLLIElement) => {
      if (loading) return;
      if (observer?.current) observer?.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        // fetch the coming ingredients if the last node and page is less than or equal to the total page.
        if (entries[0].isIntersecting && page < totalPages) {
          setPage((prev) => {
            const newPage = prev + 1;
            fetchIngredients(newPage, searchQuery);
            return newPage;
          });
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, page, totalPages, fetchIngredients, searchQuery],
  );

  const onSearch = useCallback(
    async (val: string) => {
      try {
        setPage(1);
        setOptions([]);
        await fetchIngredients(1, val);
      } catch (error) {
        console.log('error', error);
      }
    },
    [fetchIngredients],
  );

  useEffect(() => {
    onSearch(debounceValue);
  }, [debounceValue, onSearch]);

  const onClose = () => {
    setPage(1);
    setOptions([]);
    setSearchQuery('');
    fetchIngredients(1);
  };

  const updatedOptions = useMemo(() => {
    if (options) {
      return renderIngredients(options ?? []);
    }
    return [];
  }, [options]);

  return (
    <Autocomplete
      disableClearable
      popupIcon={<></>}
      disabled={disabled}
      multiple={isMultiple}
      value={selectedOptions}
      renderOption={(props, option) => {
        const { value: val, name: ingredName } = option || {};
        return (
          <MenuItem {...props} key={val} ref={lastElementRef}>
            {ingredName}
          </MenuItem>
        );
      }}
      options={getLoading ? [] : updatedOptions}
      renderTags={() => <></>}
      onClose={onClose}
      getOptionLabel={(option) => option.name || ''}
      loading={loading}
      onChange={(_, value, reason, detail) => {
        const { option } = detail || {};
        const { name, potency, value: ingredientId, isActive, overage, gummyOverage } = option || {};
        setSelectedOptions(value as IngredientItemFormType[]);
        handleChange &&
          handleChange(
            {
              showCfu: false,
              type: '',
              value: '',
              name: name || '',
              potency: potency ?? 1,
              unit: FormulaIngredientUnit.Mg,
              ingredientPotency: potency ?? 1,
              ingredientId: ingredientId || '',
              isActive: isActive,
              ...(overage ? { overage } : {}),
              ...(gummyOverage ? { gummyOverage } : {}),
            },
            reason,
          );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          margin={margin}
          value={searchQuery}
          variant="outlined"
          label={isRequired ? `${title} *` : title}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <Fragment>
                <Box px={1} display="flex" alignItems="center">
                  <SearchInputIcon />
                </Box>
                {params.InputProps.startAdornment}
              </Fragment>
            ),
            endAdornment: (
              <Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}>
          {updatedOptions?.map((item) => {
            const { name, value } = item;
            return (
              <MenuItem key={value} value={value}>
                {name}
              </MenuItem>
            );
          })}
        </TextField>
      )}
    />
  );
};

export default IngredientSelector;
